// ============================================================
// calls.js — Colonne de liste des parcs (côté gauche de l'écran)
// Composant de classe qui affiche, filtre et trie la liste des
// parcs reçue en props depuis App.js. Gère également les trois
// modales (contrôle, suppression, modification) et les appels API
// correspondants.
// Props :
//   clients                — tableau de tous les parcs (depuis App.js)
//   filterControl          — filtre de statut (FILTER_*)
//   filterClient           — texte de recherche par nom
//   filterUserControl      — filtre par expert (null = tous)
//   authtoken              — token Bearer Auth0
//   onRefresh              — callback pour recharger les parcs depuis App.js
//   onFilterChange         — callback pour mettre à jour la recherche texte
//   onFilterUserControlChange — callback pour changer le filtre expert
// ============================================================

import React from 'react';
import { colorExclamation, filterParks } from './parkUtils';
import { ListGroup, Col, Button, Form, Dropdown } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css'
import { apiSetControled, apiSetStandby, apiDeletePark } from './parkApi';
import moment from 'moment';
import { orderBy } from 'natural-orderby';
import { sortByDistance } from 'sort-by-distance';
import ParkControlModal from './ParkControlModal';
import ParkDeleteModal from './ParkDeleteModal';
import ParkEditModal from './ParkEditModal';


export default class UserList extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,   // Modale de contrôle ouverte
            isOpen2:false,   // Modale de suppression ouverte
            isOpen3:false,   // Modale de modification ouverte
            currentClient: null, // Parc actuellement sélectionné
            sortOrder: 1,        // Tri par défaut : contrôles les plus récents
            currentLat: 0,       // Latitude GPS de l'utilisateur
            currentLng: 0,       // Longitude GPS de l'utilisateur
        };
    }

    // Au montage : tente d'obtenir la position GPS de l'utilisateur
    // pour permettre le tri "parcs les plus proches"
    componentDidMount() {
        navigator.geolocation.getCurrentPosition((position) => {
            this.setState({
                currentLat: position.coords.latitude,
                currentLng: position.coords.longitude,
            });
        });
    }

    // --- Gestion des modales ---
    // Modale de contrôle (principale) : stocke le parc sélectionné
    openModal = (client) => this.setState({ isOpen: true, currentClient: client });
    closeModal = () => this.setState({ isOpen: false, currentClient: [] });

    // Modale de suppression : ne réinitialise pas currentClient
    // pour garder les données du parc visibles dans la modale principale
    openModal2 = (client) => this.setState({ isOpen2: true, currentClient: client });
    closeModal2 = () => this.setState({ isOpen2: false });

    // Modale de modification
    openModal3 = (client) => this.setState({ isOpen3: true, currentClient: client });
    closeModal3 = () => this.setState({ isOpen3: false });

    // Valide le contrôle d'un parc : envoie la date et l'expert à l'API,
    // puis ferme la modale et rafraîchit la liste
    setIsControled(id, ControlDate, userControl, exitStandby) {
        if (ControlDate === 0) {
            alert("veuillez verifier la date");
            return;
        }
        apiSetControled(this.props.authtoken, id, ControlDate, userControl, exitStandby)
            .then(() => { this.closeModal(); this.props.onRefresh(); })
            .catch(err => console.error('Erreur setIsControled:', err));
    }

    // Bascule l'état standby du parc (toggle côté serveur)
    setStandby(id) {
        apiSetStandby(this.props.authtoken, id)
            .then(() => { this.closeModal(); this.props.onRefresh(); })
            .catch(err => console.error('Erreur setStandby:', err));
    }

    // Supprime définitivement le parc, ferme les deux modales
    // (contrôle + suppression) et rafraîchit la liste
    deletePark(id) {
        apiDeletePark(this.props.authtoken, id)
            .then(() => { this.closeModal(); this.closeModal2(); this.props.onRefresh(); })
            .catch(err => console.error('Erreur deletePark:', err));
    }


    render() {
        // Point de départ : tous les parcs reçus depuis App.js
        let SortedList = this.props.clients || [];
        let filteredList = this.props.clients || [];

        // --- ÉTAPE 1 : TRI ---
        if (this.state.sortOrder === 0) {
            // Tri par distance : utilise la position GPS de l'utilisateur
            const opts = {
                yName: 'lat',
                xName: 'lng'
            }
            const origin = { lat: this.state.currentLat, lng: this.state.currentLng}
            SortedList = sortByDistance(origin, SortedList, opts);
          }
        else if (this.state.sortOrder === 1) {
            // Tri par date de contrôle décroissante (les plus récents en premier)
            SortedList = orderBy(
                SortedList,
                [v => v.ControlDate, v => v.ip],
                ['desc', 'asc']
              );
        }
        else if (this.state.sortOrder === 2) {
            // Tri alphabétique par nom (ordre naturel, gère les accents)
            SortedList = orderBy(
                SortedList,
                [v => v.name, v => v.ip],
                ['asc']
              );
        }
        else if (this.state.sortOrder === 3) {
            // Tri par date de contrôle croissante (les plus anciens en premier)
            SortedList = orderBy(
                SortedList,
                [v => v.ControlDate, v => v.ip],
                ['asc', 'asc']
              );
        }

        // --- ÉTAPES 2, 3, 4 : FILTRAGE (statut, texte, expert) ---
        filteredList = filterParks(SortedList, this.props.filterControl, this.props.filterClient, this.props.filterUserControl);

        // Version à jour du parc sélectionné : après un refresh (ex. modification),
        // les modales ouvertes reflètent les nouvelles données et non l'ancien snapshot
        const selected = this.state.currentClient;
        const currentClient = (selected && selected.id !== undefined
            && (this.props.clients || []).find(c => c.id === selected.id)) || selected;

        return (
            // Colonne liste : plein écran mobile, moitié en tablette, 1/4 en large
            <Col xs={12} md={6} lg={6} xl={3} className="list">

                {/* En-tête fixe : barre de recherche + dropdowns de tri/expert */}
                <div className="list-header">
                    {/* Champ de recherche textuelle par nom de parc */}
                    <Form style={{ marginBottom: '8px' }}>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un parc..."
                            value={this.props.filterClient || ''}
                            onChange={(e) => { if (this.props.onFilterChange) this.props.onFilterChange(e); }}
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #6c757d', borderRadius: '6px', padding: '6px 10px' }}
                        />
                    </Form>

                    {/* Conteneur responsive : empilé sur mobile, côte à côte sur tablette+ */}
                    <div className="d-flex flex-column align-items-center flex-md-row flex-md-wrap align-items-md-center" style={{ gap: '8px' }}>

                        {/* Dropdown de tri : affiche le tri actuellement sélectionné */}
                        <div>
                            <Dropdown>
                                <Dropdown.Toggle style={{ backgroundColor: "green", borderColor: "green", color: "white", fontWeight: "normal" }} id="dropdown-basic">
                                    {/* Libellé dynamique selon sortOrder */}
                                    {{ 0: 'Parcs les plus proches', 1: 'Contrôles les plus récents', 2: 'Ordre alphabétique', 3: 'Contrôles les plus anciens' }[this.state.sortOrder] || 'Tri'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {/* Option sélectionnée : disabled → fond gris via CSS .dropdown-item:disabled */}
                                    <Dropdown.Item disabled={this.state.sortOrder === 1} onClick={() => this.setState({sortOrder:1})}>Contrôles les plus récents</Dropdown.Item>
                                    <Dropdown.Item disabled={this.state.sortOrder === 3} onClick={() => this.setState({sortOrder:3})}>Contrôles les plus anciens</Dropdown.Item>
                                    <Dropdown.Item disabled={this.state.sortOrder === 2} onClick={() => this.setState({sortOrder:2})}>Par ordre alphabétique</Dropdown.Item>
                                    <Dropdown.Item disabled={this.state.sortOrder === 0} onClick={() => this.setState({sortOrder:0})}>Parcs les plus proches</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Dropdown de filtrage par expert : affiche l'expert sélectionné */}
                        <div>
                            <Dropdown>
                                <Dropdown.Toggle style={{ backgroundColor: "green", borderColor: "green", color: "white", fontWeight: "normal" }} id="dropdown-expert">
                                    {/* Affiche le nom de l'expert ou "Tous les experts" si null */}
                                    {this.props.filterUserControl
                                        ? ((this.props.experts || []).find(function(e) { return e.id === this.props.filterUserControl; }, this) || {}).name || 'Tous les experts'
                                        : 'Tous les experts'}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item disabled={!this.props.filterUserControl} onClick={() => this.props.onFilterUserControlChange(null)}>Tous les experts</Dropdown.Item>
                                    {(this.props.experts || []).map((e) => (
                                        <Dropdown.Item key={e.id} disabled={this.props.filterUserControl === e.id} onClick={() => this.props.onFilterUserControlChange(e.id)}>Contrôlé par {e.name}</Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>

                {/* Corps scrollable : liste des parcs filtrés et triés */}
                <div className="list-body">
                <ListGroup>
                    {filteredList.map((client, i) => (
                    // Chaque item : bande colorée gauche selon l'état du parc
                    <ListGroup.Item key={client.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', borderLeft: `8px solid ${colorExclamation(client)}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Nom du parc */}
                            <div>{client.name}</div>
                            {/* Date et expert du dernier contrôle (format DD/MM/YYYY) */}
                            <div style={{ fontSize: '0.82rem', color: '#555' }}>Contrôlé le {moment(client.ControlDate).format("DD/MM/YYYY")} par {client.userControlName}</div>
                        </div>
                        {/* Bouton "Ouvrir" : ouvre la modale de contrôle pour ce parc */}
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <Button style={{ backgroundColor: "green", borderColor: "green", color: "white", padding: "4px 12px" }} onClick={() => this.openModal(client)}>Ouvrir</Button>
                        </div>
                    </ListGroup.Item>
                ))}
                </ListGroup>
                </div>

                {/* Modale principale de contrôle — une seule instance, partagée */}
                <ParkControlModal
                    show={this.state.isOpen}
                    onHide={this.closeModal}
                    client={currentClient}
                    experts={this.props.experts}
                    onSetIsControled={(date, userControlId, exitStandby) => this.setIsControled(currentClient.id, date, userControlId, exitStandby)}
                    onSetStandby={() => this.setStandby(currentClient.id)}
                    onOpenDelete={(client) => this.openModal2(client)}
                    onOpenEdit={(client) => this.openModal3(client)}
                    role={this.props.role}
                    userEmail={this.props.userEmail}
                />

                {/* Modale de confirmation de suppression */}
                <ParkDeleteModal
                    show={this.state.isOpen2}
                    onHide={this.closeModal2}
                    client={currentClient}
                    onDelete={() => this.deletePark(currentClient.id)}
                />

                {/* Modale de modification du parc */}
                <ParkEditModal
                    show={this.state.isOpen3}
                    onHide={this.closeModal3}
                    client={currentClient}
                    experts={this.props.experts}
                    authtoken={this.props.authtoken}
                    onRefresh={this.props.onRefresh}
                />
            </Col>
        )
    }
}
